import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Pagination, IconButton, MenuItem, Paper, Step, StepLabel, Stepper, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { BillOfEntryService } from '../../../services/billOfEntry.service';
import { SupplierBillService } from '../../../services/supplierBill.service';
import { SupplierService } from '../../../services/supplier.service';
import { AccountService } from '../../../services/account.service';
import { AuthService } from '../../../services/auth.service';
import { Supplier } from '../../../interfaces/procurement/supplier.interface';
import { Account } from '../../../interfaces/accounting.interface';
import {
  AllocationMethod, AllocationPreview, CnfBillOption, CREDIT_SOURCE_LABELS, PCT_BASE, TREATMENT_LABELS, BillOfEntryRequest, GrnLineOption, BoeChargeInput, CHARGE_KINDS, ChargeKind,
  ChargeTreatment, CREDIT_SOURCES, CreditSource, defaultTreatment,
} from '../../../interfaces/procurement/billOfEntry.interface';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import SearchableSelect from '../../common/SearchableSelect';
import { leafAccounts } from '../../../utils/postingAccounts';
import { useToast } from '../../../context/ToastContext';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { toLocalIsoDate } from '../../../utils/dateUtils';

const STEPS = ['Header & goods receipts', 'Charges', 'Allocation', 'Review'];
const METHODS: AllocationMethod[] = ['VALUE', 'QTY', 'WEIGHT', 'MANUAL'];
const TREATMENTS: ChargeTreatment[] = ['CAPITALISE', 'INPUT_VAT', 'ADVANCE_INCOME_TAX'];

interface LineState { goodsReceiptLineId: string; grnNumber: string; itemName: string; qty: number; value: number;
  selected: boolean; weight: string; manualAmount: string; }
interface ChargeState { kind: ChargeKind; treatment: ChargeTreatment; creditSource: CreditSource; amount: string;
  description: string; supplierBillId: string; supplierId: string; bankGlAccountId: string; reference: string;
  /** Optional % helper; while set (and the amount not typed by hand) the amount follows it. */
  pct: string; amountTyped: boolean; }

/** Re-derives every %-driven charge: CD/RD/SD/AIT from the assessable value, VAT/AT from AV + CD + RD + SD. */
const applyPct = (cs: ChargeState[], av: number): ChargeState[] => {
  const round = (n: number) => String(Math.round(n * 100) / 100);
  let next = cs.map(c => ({ ...c }));
  const duties = () => next.filter(c => c.kind === 'CD' || c.kind === 'RD' || c.kind === 'SD')
    .reduce((a, c) => a + (Number(c.amount) > 0 ? Number(c.amount) : 0), 0);
  for (const pass of ['AV', 'AV_PLUS_DUTIES'] as const) {
    next = next.map(c => {
      if (PCT_BASE[c.kind] !== pass || c.pct.trim() === '' || c.amountTyped || !(Number(c.pct) >= 0)) return c;
      const base = pass === 'AV' ? av : av + duties();
      return { ...c, amount: round((base * Number(c.pct)) / 100) };
    });
  }
  return next;
};

const emptyCharge = (): ChargeState => ({ kind: 'CD', treatment: 'CAPITALISE', creditSource: 'CUSTOMS', amount: '',
  description: '', supplierBillId: '', supplierId: '', bankGlAccountId: '', reference: '', pct: '', amountTyped: false });

export const BillOfEntryWizard: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const institutionId = AuthService.getSelectedInstitution() ?? '';
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [boeNumber, setBoeNumber] = useState('');
  const [boeDate, setBoeDate] = useState(toLocalIsoDate());
  const [customsHouse, setCustomsHouse] = useState('');
  const [assessableValue, setAssessableValue] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const [exchangeRate, setExchangeRate] = useState('');
  const [notes, setNotes] = useState('');
  const [method, setMethod] = useState<AllocationMethod>('VALUE');
  // Chosen lines live in their own list so paging/searching the picker never drops a selection.
  const [lines, setLines] = useState<LineState[]>([]);
  const [options, setOptions] = useState<GrnLineOption[]>([]);
  const [search, setSearch] = useState('');
  const [optPage, setOptPage] = useState(0);
  const [optPages, setOptPages] = useState(0);
  const [optLoading, setOptLoading] = useState(false);
  const [charges, setCharges] = useState<ChargeState[]>([emptyCharge()]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [cnfOptions, setCnfOptions] = useState<Record<string, CnfBillOption[]>>({});
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [preview, setPreview] = useState<AllocationPreview | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const [supPage, accs] = await Promise.all([
          SupplierService.listByInstitution(0, 200),
          AccountService.getAllAccounts(),
        ]);
        setSuppliers(supPage?.content ?? []);
        setAccounts(accs ?? []);
        if (id) {
          const boe = await BillOfEntryService.getById(id);
          setBoeNumber(boe.boeNumber); setBoeDate(boe.boeDate); setCustomsHouse(boe.customsHouse ?? '');
          setAssessableValue(boe.assessableValue != null ? String(boe.assessableValue) : '');
          setCurrencyCode(boe.currencyCode ?? ''); setExchangeRate(boe.exchangeRate != null ? String(boe.exchangeRate) : '');
          setNotes(boe.notes ?? ''); setMethod(boe.allocationMethod);
          // Seed from the saved lines so they are never dropped, whatever the picker currently shows.
          setLines(boe.lines.map(l => ({
            goodsReceiptLineId: l.goodsReceiptLineId, grnNumber: l.grnNumber ?? '', itemName: l.itemName ?? l.itemId,
            qty: l.receivedQty, value: l.lineValue, selected: true,
            weight: l.weight != null ? String(l.weight) : '', manualAmount: l.manualAmount != null ? String(l.manualAmount) : '',
          })));
          // The saved charge knows its bill but not its supplier: read it from the bill so the picker can filter.
          const billSupplier: Record<string, string> = {};
          for (const bid of Array.from(new Set(boe.charges.map(c => c.supplierBillId).filter((x): x is string => !!x)))) {
            try { billSupplier[bid] = (await SupplierBillService.getById(bid)).supplierId; } catch { /* picker stays empty */ }
          }
          setCharges(boe.charges.map(c => ({ kind: c.kind, treatment: c.treatment, creditSource: c.creditSource,
            amount: String(c.amount), description: c.description ?? '', supplierBillId: c.supplierBillId ?? '',
            supplierId: c.supplierBillId ? billSupplier[c.supplierBillId] ?? '' : '',
            bankGlAccountId: c.bankGlAccountId ?? '', reference: c.reference ?? '', pct: '', amountTyped: true })));
        }
      } catch (e) {
        toast.showApiError(e, 'Failed to load bill of entry data');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side search of POSTED goods receipt lines (debounced).
  useEffect(() => {
    if (!institutionId) return undefined;
    const t = setTimeout(async () => {
      try {
        setOptLoading(true);
        const res = await BillOfEntryService.grnLines(institutionId, search.trim(), optPage, 20);
        setOptions(res.content);
        setOptPages(res.totalPages);
      } catch (e) {
        toast.showApiError(e, 'Failed to load goods receipt lines');
      } finally {
        setOptLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, optPage, institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOption = (o: GrnLineOption, checked: boolean) => setLines(ls => checked
    ? (ls.some(l => l.goodsReceiptLineId === o.goodsReceiptLineId) ? ls : [...ls, {
      goodsReceiptLineId: o.goodsReceiptLineId, grnNumber: o.grnNumber ?? '', itemName: o.itemName ?? o.itemId,
      qty: o.receivedQty, value: o.lineValue, selected: true, weight: '', manualAmount: '' }])
    : ls.filter(l => l.goodsReceiptLineId !== o.goodsReceiptLineId));

  const postingAccounts = useMemo(() => leafAccounts(accounts), [accounts]);
  const selected = lines;

  const toNum = (s: string): number | null => (s.trim() === '' || !Number.isFinite(Number(s)) ? null : Number(s));

  const buildRequest = (): BillOfEntryRequest => ({
    institutionId, boeNumber: boeNumber.trim(), boeDate, customsHouse: customsHouse || undefined,
    assessableValue: toNum(assessableValue), currencyCode: currencyCode || undefined, exchangeRate: toNum(exchangeRate),
    allocationMethod: method, notes: notes || undefined,
    lines: selected.map(l => ({ goodsReceiptLineId: l.goodsReceiptLineId, weight: toNum(l.weight),
      manualAmount: toNum(l.manualAmount) })),
    charges: charges.filter(c => toNum(c.amount) != null).map((c): BoeChargeInput => ({
      kind: c.kind, treatment: c.treatment, creditSource: c.creditSource, amount: Number(c.amount),
      description: c.description || undefined, supplierBillId: c.creditSource === 'SUPPLIER_BILL' ? c.supplierBillId || null : null,
      bankGlAccountId: c.creditSource === 'BANK' ? c.bankGlAccountId || null : null, reference: c.reference || undefined,
    })),
  });

  const headerValid = boeNumber.trim() !== '' && boeDate !== '' && selected.length > 0;
  const chargesValid = charges.some(c => toNum(c.amount) != null) && charges.every(c => toNum(c.amount) == null
    || (c.creditSource !== 'BANK' || c.bankGlAccountId !== '') && (c.creditSource !== 'SUPPLIER_BILL' || c.supplierBillId !== ''));

  const runPreview = async () => {
    try { setPreview(await BillOfEntryService.preview(buildRequest())); }
    catch (e) { setPreview(null); toast.showApiError(e, 'Could not preview the allocation'); }
  };

  useEffect(() => { if (step >= 2 && headerValid && chargesValid) runPreview(); },
    [step, method]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      setSaving(true);
      const saved = id ? await BillOfEntryService.update(id, buildRequest()) : await BillOfEntryService.create(buildRequest());
      toast.showSuccess('Bill of entry saved as draft');
      navigate(`/procurement/bills-of-entry/${saved.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to save bill of entry');
    } finally {
      setSaving(false);
    }
  };

  const setLine = (i: number, patch: Partial<LineState>) => setLines(ls => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  /** Assessable value in BDT: foreign value x rate when a currency rate is given. */
  const assessableBase = useMemo(() => {
    const av = Number(assessableValue);
    if (!(av > 0)) return 0;
    const rate = Number(exchangeRate);
    return rate > 0 ? av * rate : av;
  }, [assessableValue, exchangeRate]);

  const supplierName = (sid: string) => suppliers.find(s => s.id === sid)?.name ?? 'Supplier';

  const setCharge = (i: number, patch: Partial<ChargeState>) =>
    setCharges(cs => applyPct(cs.map((c, k) => (k === i ? { ...c, ...patch } : c)), assessableBase));

  // The assessable value or rate changed: re-derive the %-driven amounts.
  useEffect(() => { setCharges(cs => applyPct(cs, assessableBase)); }, [assessableBase]);

  // C&F bills of the chosen supplier(s) that debited import clearing, with their free amounts.
  useEffect(() => {
    const wanted = Array.from(new Set(charges.filter(c => c.creditSource === 'SUPPLIER_BILL' && c.supplierId)
      .map(c => c.supplierId))).filter(s => !(s in cnfOptions));
    wanted.forEach(async supplierId => {
      try {
        const res = await BillOfEntryService.cnfBills(institutionId, supplierId, id);
        setCnfOptions(o => ({ ...o, [supplierId]: res }));
      } catch (e) {
        setCnfOptions(o => ({ ...o, [supplierId]: [] }));
        toast.showApiError(e, 'Failed to load the supplier\'s C&F bills');
      }
    });
  }, [charges]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const canNext = step === 0 ? headerValid : step === 1 ? chargesValid : true;

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.boeForm.page">
      <Typography variant="h5" sx={{ mb: 2 }}>{id ? 'Edit Bill of Entry' : 'New Bill of Entry'}</Typography>
      <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
        {STEPS.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>

      {step === 0 && (
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="procurement.boeForm.section.header">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField data-help="procurement.boeForm.field.number" label="BoE number" required value={boeNumber} onChange={e => setBoeNumber(e.target.value)} sx={{ flex: '1 1 200px', maxWidth: 280 }} />
            <TextField data-help="procurement.boeForm.field.date" label="BoE date" type="date" required value={boeDate} onChange={e => setBoeDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 180px', maxWidth: 220 }} />
            <TextField data-help="procurement.boeForm.field.house" label="Customs house" value={customsHouse} onChange={e => setCustomsHouse(e.target.value)} sx={{ flex: '1 1 220px', maxWidth: 320 }} />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField data-help="procurement.boeForm.field.assessable" label="Assessable value (foreign)" type="number" value={assessableValue} onChange={e => setAssessableValue(e.target.value)} sx={{ flex: '1 1 200px', maxWidth: 260 }} />
            <TextField data-help="procurement.boeForm.field.currency" label="Currency" value={currencyCode} onChange={e => setCurrencyCode(e.target.value.toUpperCase().slice(0, 3))} sx={{ flex: '1 1 100px', maxWidth: 140 }} />
            <TextField data-help="procurement.boeForm.field.rate" label="Exchange rate" type="number" value={exchangeRate} onChange={e => setExchangeRate(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
          </Box>
          <TextField data-help="procurement.boeForm.field.notes" label="Notes" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={2} />
          <Typography variant="subtitle1">Posted goods receipt lines this bill of entry covers</Typography>
          <TextField data-help="procurement.boeForm.field.search" label="Search goods receipts (GRN number or item)" value={search} sx={{ flex: '1 1 260px', maxWidth: 420 }}
            onChange={e => { setSearch(e.target.value); setOptPage(0); }} />
          {optLoading && <CircularProgress size={20} />}
          {!optLoading && options.length === 0 && <Alert severity="info">No posted goods receipt lines found.</Alert>}
          <Table size="small" data-help-section="procurement.boeForm.pick">
            <TableHead><TableRow><TableCell data-help="procurement.boeForm.pick.select" /><TableCell data-help="procurement.boeForm.pick.grn">GRN</TableCell><TableCell data-help="procurement.boeForm.pick.item">Item</TableCell>
              <TableCell data-help="procurement.boeForm.pick.qty" align="right">Qty</TableCell><TableCell data-help="procurement.boeForm.pick.value" align="right">Value</TableCell></TableRow></TableHead>
            <TableBody>
              {options.map(o => (
                <TableRow key={o.goodsReceiptLineId} hover>
                  <TableCell padding="checkbox"><Checkbox data-help="procurement.boeForm.pick.check" checked={lines.some(l => l.goodsReceiptLineId === o.goodsReceiptLineId)}
                    inputProps={{ 'aria-label': `Select ${o.grnNumber} ${o.itemName}` }}
                    onChange={e => toggleOption(o, e.target.checked)} /></TableCell>
                  <TableCell>{o.grnNumber}</TableCell><TableCell>{o.itemName}</TableCell>
                  <TableCell align="right">{o.receivedQty}</TableCell><TableCell align="right">{formatMoney(o.lineValue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {optPages > 1 && <Pagination count={optPages} page={optPage + 1} onChange={(_e, p) => setOptPage(p - 1)} />}
          <Typography variant="subtitle2">Chosen: {lines.length} line(s)</Typography>
        </Paper>
      )}

      {step === 1 && (
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="procurement.boeForm.section.charges">
          {charges.map((c, i) => (
            <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <TextField data-help="procurement.boeForm.field.kind" select label="Kind" value={c.kind} sx={{ flex: '1 1 110px', maxWidth: 140 }}
                onChange={e => { const k = e.target.value as ChargeKind; setCharge(i, { kind: k, treatment: defaultTreatment(k) }); }}>
                {CHARGE_KINDS.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </TextField>
              <TextField data-help="procurement.boeForm.field.treatment" select label="Treatment" value={c.treatment} sx={{ flex: '1 1 200px', maxWidth: 260 }}
                onChange={e => setCharge(i, { treatment: e.target.value as ChargeTreatment })}>
                {TREATMENTS.map(t => <MenuItem key={t} value={t}>{TREATMENT_LABELS[t]}</MenuItem>)}
              </TextField>
              {PCT_BASE[c.kind] && (
                <TextField data-help="procurement.boeForm.field.pct" label={PCT_BASE[c.kind] === 'AV' ? '% of assessable value' : '% of AV + duties'} type="number" value={c.pct}
                  sx={{ flex: '1 1 120px', maxWidth: 160 }} disabled={assessableBase <= 0}
                  helperText={assessableBase <= 0 ? 'Enter the assessable value first' : undefined}
                  onChange={e => setCharge(i, { pct: e.target.value, amountTyped: false })} />
              )}
              <TextField data-help="procurement.boeForm.field.amount" label="Amount (BDT)" type="number" value={c.amount} sx={{ flex: '1 1 140px', maxWidth: 180 }}
                onChange={e => setCharge(i, { amount: e.target.value, amountTyped: true })} />
              <TextField data-help="procurement.boeForm.field.via" select label="Paid / owed via" value={c.creditSource} sx={{ flex: '1 1 160px', maxWidth: 200 }}
                onChange={e => setCharge(i, { creditSource: e.target.value as CreditSource })}>
                {CREDIT_SOURCES.map(s => <MenuItem key={s} value={s}>{CREDIT_SOURCE_LABELS[s]}</MenuItem>)}
              </TextField>
              {c.creditSource === 'BANK' && (
                <Box sx={{ flex: '1 1 240px', maxWidth: 340 }} data-help="procurement.boeForm.field.bankAccount" >
                  <AccountAutocomplete accounts={postingAccounts} value={c.bankGlAccountId} label="Bank / cash account" required
                    onChange={v => setCharge(i, { bankGlAccountId: v })} />
                </Box>
              )}
              {c.creditSource === 'SUPPLIER_BILL' && (
                <>
                  <Box sx={{ flex: '1 1 220px', maxWidth: 300 }} data-help="procurement.boeForm.field.cnfSupplier" >
                    <SearchableSelect<Supplier> options={suppliers} value={c.supplierId} label="C&F agent (supplier)" required
                      getOptionLabel={s => s.name} onChange={v => setCharge(i, { supplierId: v, supplierBillId: '' })} />
                  </Box>
                  <Box sx={{ flex: '1 1 260px', maxWidth: 380 }} data-help="procurement.boeForm.field.cnfBill" >
                    <SearchableSelect<CnfBillOption> options={cnfOptions[c.supplierId] ?? []} value={c.supplierBillId}
                      label="C&F agent bill" required disabled={!c.supplierId} getOptionId={b => b.billId}
                      noOptionsText="No posted bill with an import clearing debit - enter a service bill first"
                      getOptionLabel={b => `${b.billNumber} \u00b7 ${supplierName(c.supplierId)} \u00b7 ${formatMoney(b.clearingDebit)} to import clearing \u00b7 ${formatMoney(b.available)} still available`}
                      onChange={(v, opt) => setCharge(i, {
                        supplierBillId: v,
                        // Prefill with what the bill still has free; the amount stays editable.
                        ...(opt ? { amount: String(opt.available), amountTyped: true } : {}),
                      })} />
                  </Box>
                </>
              )}
              <TextField data-help="procurement.boeForm.field.chargeDesc" label="Description" value={c.description} sx={{ flex: '2 1 260px', maxWidth: 520 }}
                onChange={e => setCharge(i, { description: e.target.value })} />
              <IconButton data-help="procurement.boeForm.removeCharge" aria-label="Remove charge" disabled={charges.length === 1}
                onClick={() => setCharges(cs => cs.filter((_x, k) => k !== i))}><DeleteIcon /></IconButton>
            </Box>
          ))}
          <Box><Button data-help="procurement.boeForm.addCharge" startIcon={<AddIcon />} onClick={() => setCharges(cs => [...cs, emptyCharge()])}>Add charge</Button></Box>
          <Alert severity="info">
            Capitalise adds the charge to stock cost. VAT and AT become a recoverable input tax credit; AIT becomes an
            advance income tax receivable. A C&F agent bill routes through the import clearing account.
          </Alert>
        </Paper>
      )}

      {step === 2 && (
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="procurement.boeForm.section.allocation">
          <TextField data-help="procurement.boeForm.field.method" select label="Allocation method" value={method} sx={{ flex: '1 1 220px', maxWidth: 260 }}
            onChange={e => setMethod(e.target.value as AllocationMethod)}>
            {METHODS.map(m => <MenuItem key={m} value={m}>{humanizeEnum(m)}</MenuItem>)}
          </TextField>
          <Table size="small">
            <TableHead><TableRow><TableCell data-help="procurement.boeForm.alloc.grn">GRN</TableCell><TableCell data-help="procurement.boeForm.alloc.item">Item</TableCell>
              <TableCell data-help="procurement.boeForm.alloc.qty" align="right">Qty</TableCell><TableCell data-help="procurement.boeForm.alloc.value" align="right">Value</TableCell>
              {method === 'WEIGHT' && <TableCell data-help="procurement.boeForm.alloc.weight">Weight</TableCell>}
              {method === 'MANUAL' && <TableCell data-help="procurement.boeForm.alloc.manual">Manual amount</TableCell>}
              <TableCell data-help="procurement.boeForm.alloc.amount" align="right">Allocated</TableCell></TableRow></TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={l.goodsReceiptLineId}>
                  <TableCell>{l.grnNumber}</TableCell><TableCell>{l.itemName}</TableCell>
                  <TableCell align="right">{l.qty}</TableCell><TableCell align="right">{formatMoney(l.value)}</TableCell>
                  {method === 'WEIGHT' && <TableCell><TextField data-help="procurement.boeForm.field.weight" size="small" type="number" value={l.weight}
                    inputProps={{ 'aria-label': `Weight ${l.itemName}` }} onChange={e => setLine(i, { weight: e.target.value })} /></TableCell>}
                  {method === 'MANUAL' && <TableCell><TextField data-help="procurement.boeForm.field.manual" size="small" type="number" value={l.manualAmount}
                    inputProps={{ 'aria-label': `Manual ${l.itemName}` }} onChange={e => setLine(i, { manualAmount: e.target.value })} /></TableCell>}
                  <TableCell align="right">{formatMoney(preview?.lines.find(p => p.goodsReceiptLineId === l.goodsReceiptLineId)?.allocatedAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box><Button data-help="procurement.boeForm.preview" variant="outlined" onClick={runPreview}>Refresh preview</Button></Box>
          {preview?.warnings.map(w => <Alert severity="warning" key={w}>{w}</Alert>)}
        </Paper>
      )}

      {step === 3 && (
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }} data-help-section="procurement.boeForm.section.review">
          <Typography>BoE {boeNumber} dated {boeDate}, {selected.length} goods receipt line(s), {humanizeEnum(method)} allocation.</Typography>
          {preview ? (
            <>
              <Typography>Capitalised into stock: {formatMoney(preview.capitalisePool)}</Typography>
              <Typography>Recoverable VAT / AT: {formatMoney(preview.inputTaxTotal)}</Typography>
              <Typography>Advance income tax: {formatMoney(preview.aitTotal)}</Typography>
              <Typography>Owed to customs: {formatMoney(preview.customsCredit)} | paid from bank: {formatMoney(preview.bankCredit)} | via C&F bill (clearing): {formatMoney(preview.clearingCredit)}</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell data-help="procurement.boeForm.review.grn">GRN</TableCell><TableCell data-help="procurement.boeForm.review.item">Item</TableCell>
                  <TableCell data-help="procurement.boeForm.review.value" align="right">Value</TableCell><TableCell data-help="procurement.boeForm.review.alloc" align="right">Allocated cost</TableCell></TableRow></TableHead>
                <TableBody>
                  {lines.map(l => (
                    <TableRow key={l.goodsReceiptLineId}>
                      <TableCell>{l.grnNumber}</TableCell><TableCell>{l.itemName}</TableCell>
                      <TableCell align="right">{formatMoney(l.value)}</TableCell>
                      <TableCell align="right">{formatMoney(preview.lines.find(p => p.goodsReceiptLineId === l.goodsReceiptLineId)?.allocatedAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {preview.warnings.map(w => <Alert severity="warning" key={w}>{w}</Alert>)}
            </>
          ) : <Alert severity="info">Preview unavailable.</Alert>}
          <Alert severity="info">Saving creates a draft. Post it from the detail page to book the journal and update stock cost.</Alert>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button data-help="procurement.boeForm.cancel" onClick={() => navigate('/procurement/bills-of-entry')}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button data-help="procurement.boeForm.back" disabled={step === 0} onClick={() => setStep(s => s - 1)}>Back</Button>
        {step < STEPS.length - 1
          ? <Button data-help="procurement.boeForm.next" variant="contained" disabled={!canNext} onClick={() => setStep(s => s + 1)}>Next</Button>
          : <Button data-help="procurement.boeForm.save" variant="contained" disabled={saving || !headerValid || !chargesValid} onClick={save}>Save draft</Button>}
      </Box>
    </Box>
  );
};

export default BillOfEntryWizard;
